/**
 * The map's tile cache, and the queue that fills it from the map worker.
 *
 * Tiles are requested only while they are wanted — a request made for a view
 * the player has already panned away from is dropped before it starts — and
 * kept in a bounded cache, least recently used first out.
 */

import { WorkerPool } from '../workers/pool.js';
import type { MapLabel, MapTileJob, MapTileResult } from './tileRenderer.js';

const TILE = 256;
const CAPACITY = 220;
const IN_FLIGHT = 6;

interface Entry {
  bitmap: ImageBitmap;
  labels: MapLabel[];
  used: number;
}

export class MapTiles {
  private readonly pool = new WorkerPool<MapTileJob, MapTileResult>(
    () => new Worker(new URL('../workers/maptile.worker.ts', import.meta.url), { type: 'module' }),
    3,
  );
  private readonly cache = new Map<string, Entry>();
  private readonly wanted = new Map<string, { z: number; x: number; y: number; priority: number }>();
  private readonly loading = new Set<string>();
  private readonly failed = new Map<string, number>();
  private clock = 0;
  /** Bumped whenever a tile arrives, so views know to redraw. */
  version = 0;

  constructor(
    private readonly vectorTemplate: string,
    private readonly elevationTemplate: string,
    private readonly getDay: () => number,
  ) {}

  static key(z: number, x: number, y: number): string {
    return `${z}/${x}/${y}`;
  }

  get(z: number, x: number, y: number): Entry | undefined {
    const entry = this.cache.get(MapTiles.key(z, x, y));
    if (entry !== undefined) entry.used = ++this.clock;
    return entry;
  }

  /** Ask for a tile; lower priority numbers load first. */
  want(z: number, x: number, y: number, priority: number): void {
    const k = MapTiles.key(z, x, y);
    if (this.cache.has(k) || this.loading.has(k)) return;
    if ((this.failed.get(k) ?? 0) > performance.now()) return;
    const prior = this.wanted.get(k);
    if (prior === undefined || priority < prior.priority) this.wanted.set(k, { z, x, y, priority });
  }

  /** Start the most urgent requests. Call once a frame, after the views ask. */
  pump(): void {
    if (this.loading.size >= IN_FLIGHT || this.wanted.size === 0) {
      this.wanted.clear();
      return;
    }
    const queue = [...this.wanted.entries()].sort((a, b) => a[1].priority - b[1].priority);
    this.wanted.clear();
    for (const [k, t] of queue) {
      if (this.loading.size >= IN_FLIGHT) break;
      this.loading.add(k);
      this.pool.run({
        z: t.z, x: t.x, y: t.y, size: TILE, dayOfYear: this.getDay(),
        vectorTemplate: this.vectorTemplate, elevationTemplate: this.elevationTemplate,
      }).then((result) => {
        this.cache.set(k, { bitmap: result.bitmap, labels: result.labels, used: ++this.clock });
        this.evict();
        this.version++;
      }).catch(() => {
        // Retry later rather than hammering a tile that is not there.
        this.failed.set(k, performance.now() + 20_000);
      }).finally(() => this.loading.delete(k));
    }
  }

  private evict(): void {
    if (this.cache.size <= CAPACITY) return;
    const oldest = [...this.cache.entries()].sort((a, b) => a[1].used - b[1].used);
    for (const [k, e] of oldest.slice(0, this.cache.size - CAPACITY)) {
      e.bitmap.close();
      this.cache.delete(k);
    }
  }
}
